import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type PatchVideoDraftConfigInput,
  buildPatchVideoDraftConfigPayload,
} from './patch-video-draft-config.payload';

export type { PatchVideoDraftConfigInput };

export interface PatchVideoDraftConfigResponse {
  kind: 'video';
  itemId: string;
  attemptNumber: number;
  video: { id: string };
  rendered: boolean;
  forkedFromVideoId?: string;
  renderMessage?: string;
}

/**
 * Patches a video draft's `draftConfig` (currently the b-roll clip selection)
 * via `PATCH /videos/:id/draft-config`. The body is a partial patch deep-merged
 * server-side. Used by the assistant's video-draft-preview card, which updates
 * the local selection optimistically and fires this in the background.
 */
export const usePatchVideoDraftConfig = (options?: {
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    // Callers pass the shared intent; the one builder assembles the body.
    mutationFn: (input: PatchVideoDraftConfigInput) =>
      apiClient.patch<PatchVideoDraftConfigResponse>(
        `videos/${input.videoId}/draft-config`,
        buildPatchVideoDraftConfigPayload(input)
      ),
    onSuccess: async (data, variables) => {
      // Refresh the old cut, the edited draft, and the editor's compiled
      // preview. Otherwise reopening the same draft can show stale edits.
      void queryClient.invalidateQueries({ queryKey: ['videos'] });
      for (const id of new Set([variables.videoId, data.video.id])) {
        await queryClient.invalidateQueries({
          queryKey: ['video', id],
          exact: true,
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.content.asset('video', id),
        });
        await queryClient.invalidateQueries({
          queryKey: queryKeys.video.previewRenderDoc(id),
        });
      }
      void queryClient.invalidateQueries({
        queryKey: queryKeys.contentBatches.all(),
      });
    },
    onError: (error: Error) => {
      options?.onError?.(error);
    },
  });

  return {
    patchDraftConfig: mutation.mutate,
    patchDraftConfigAsync: mutation.mutateAsync,
    isPatching: mutation.isPending,
  };
};
